<template>
    <div
        class="report-details"
    >
        <div
            class="report-details-report"
        >
            <div
                class="deepi-row-fixed justify-between"
            >
                <p
                    v-title-clamp
                    class="text-body-l text-medium text-ellipsis"
                >
                    {{ props.report.title }}
                </p>
                <div
                    class="deepi-row align-center justify-end gap-8"
                >
                    <p
                        v-title-clamp
                        class="text-body-m text-medium text-secondary-500 text-ellipsis"
                    >
                        {{ tr(`report.category.${props.report.category.toLowerCase().replace('_', '-')}` as any) }}
                    </p>
                    <ReportStatusChip
                        :status="props.report.status"
                    />
                </div>
            </div>
            <div
                class="report-details-image"
            >
                <img
                    :src="props.report.screenshotUrl"
                />
                <p
                    v-link="[$router, props.report.pageUrl, true]"
                    v-title-clamp
                    class="text-body-s text-medium text-ellipsis"
                >
                    {{ props.report.pageUrl }}
                </p>
            </div>
            <p
                v-title-clamp
                class="text-body-m text-clamp-10"
            >
                {{ props.report.description }}
            </p>
        </div>
        <div
            class="report-details-comments"
        >
            <div
                class="report-details-user"
            >
                <p
                    v-title-clamp
                    class="text-body-m text-secondary-800 text-ellipsis"
                >
                    {{ props.report.client }} {{ props.report.organization }}
                </p>
                <p
                    v-title-clamp
                    class="text-body-m text-medium text-ellipsis"
                >
                    {{ props.report.firstname }} {{ props.report.lastname }}
                </p>
                <p
                    v-title-clamp
                    class="text-body-s text-secondary-500 text-medium text-ellipsis"
                >
                    {{ props.report.email }}
                </p>
                <p
                    v-title-clamp
                    class="text-body-s text-secondary-500 text-medium text-ellipsis"
                >
                    {{ props.report.phone }}
                </p>
                <p
                    v-title-clamp
                    class="text-body-s text-secondary-500 text-medium text-ellipsis"
                >
                    {{ props.report.createdAt.formatLocale('L HH:mm') }}
                </p>
            </div>
            <div
                class="report-details-settings"
            >
                <div
                    class="deepi-row gap-24"
                >
                    <div
                        class="deepi-col gap-4"
                    >
                        <p
                            v-title-clamp
                            class="text-body-s text-medium text-ellipsis"
                        >
                            {{ tr('report.properties.browser') }}
                        </p>
                        <p
                            v-title-clamp
                            class="text-body-s text-medium text-ellipsis"
                        >
                            {{ tr('report.properties.operating-system') }}
                        </p>
                        <p
                            v-title-clamp
                            class="text-body-s text-medium text-ellipsis"
                        >
                            {{ tr('report.properties.window-size') }}
                        </p>
                    </div>
                    <div
                        class="deepi-col gap-4"
                    >
                        <p
                            v-title-clamp
                            class="text-body-s text-secondary-500 text-medium text-ellipsis"
                        >
                            {{ props.report.settings.browser }}
                        </p>
                        <p
                            v-title-clamp
                            class="text-body-s text-secondary-500 text-medium text-ellipsis"
                        >
                            {{ props.report.settings.os }}
                        </p>
                        <p
                            v-title-clamp
                            class="text-body-s text-secondary-500 text-medium text-ellipsis"
                        >
                            {{ props.report.settings.width }} × {{ props.report.settings.height }}
                        </p>
                    </div>
                </div>
            </div>
            <div
                class="deepi-row-fixed align-center gap-8"
            >
                <p
                    v-title-clamp
                    class="text-body-m text-medium text-ellipsis"
                >
                    {{ tr('report.conversation.title') }}
                </p>
            </div>
            <div
                ref="conversationRef"
                class="report-details-conversation"
            >
                <template
                    v-for="comment in comments"
                    :key="comment.id"
                >
                    <div
                        v-if="comment.userId !== currentUser?.id"
                        class="report-details-conversation-message"
                    >
                        <p
                            v-title-clamp
                            class="text-body-xs text-medium text-secondary-800 text-ellipsis"
                        >
                            {{ comment.firstname }} {{ comment.lastname }}
                        </p>
                        <p
                            v-title-clamp
                            class="text-body-m text-ellipsis"
                        >
                            {{ comment.comment }}
                        </p>
                        <div
                            class="deepi-row justify-end"
                        >
                            <p
                                v-title-clamp
                                class="text-body-xxs text-secondary-500 text-ellipsis"
                            >
                                {{ comment.createdAt.formatLocale('L HH:mm') }}
                            </p>
                        </div>
                    </div>
                    <div
                        v-else
                        class="report-details-conversation-own-message"
                    >
                        <p
                            v-title-clamp
                            class="text-body-m text-edit text-ellipsis"
                        >
                            {{ comment.comment }}
                        </p>
                        <div
                            class="deepi-row justify-end"
                        >
                            <p
                                v-title-clamp
                                class="text-body-xxs text-edit text-ellipsis"
                            >
                                {{ comment.createdAt.formatLocale('L HH:mm') }}
                            </p>
                        </div>
                    </div>
                </template>
            </div>
            <div
                class="deepi-row-fixed align-center gap-8"
            >
                <DeepiTextArea
                    :rows="2"
                    :placeholder="tr('report.conversation.placeholder')"
                    :validateKeyDown="onKeyDown"
                    v-model="comment"
                />
                <DeepiButtonIcon
                    icon="icon-log"
                    :disabled="!comment"
                    @click="createComment"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import { useFetchClient } from '@deepidoo/playdoo/services/composables';
import { useUserStore } from '@deepidoo/playdoo/stores';

import { useTranslations } from '../../composables';
import { Comment, type CommentDTO, type Report } from '../../models';

import ReportStatusChip from '../reportStatusChip/ReportStatusChip.vue';

export interface ReportDetailsProps {
    report: Report;
};

const props = defineProps<ReportDetailsProps>();

const { post } = useFetchClient();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);
const { tr } = useTranslations();

const conversationRef = ref<HTMLDivElement | null>(null);

const comments = ref<Comment[]>([]);
const comment = ref<string>('');

const getManyComments = async (): Promise<void> => {
	const response = await post<{ data: { comments: CommentDTO[] }}>(
		`${import.meta.env.VITE_REPORT_URL}graphql`,
		{
			query: `
				query Comments($reportId: ID!) {
					comments(reportId: $reportId) {
						id
                        reportId
                        comment
                        userId
                        firstname
                        lastname
                        email
                        phone
                        phoneCountry
                        createdAt
					}
				}
			`,
			variables: {
				reportId: props.report.id
			}
		}
	);

    if (response.data && response.data.comments) {
        comments.value = response.data.comments.map(dto => new Comment(dto))
            .sort((a, b) => a.createdAt.diff(b.createdAt));
    }
};

const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        createComment();
    }
};

const createComment = async (): Promise<void> => {
    if (!currentUser.value) {
        return;
    }
    const response = await post<{ data: { createComment: CommentDTO }}>(
        `${import.meta.env.VITE_REPORT_URL}graphql`,
        {
            query: `
                mutation CreateComment($input: CreateCommentDTO!) {
                    createComment(input: $input) {
                        id
                        reportId
                        comment
                        userId
                        firstname
                        lastname
                        email
                        phone
                        phoneCountry
                        createdAt
                    }
                }
            `,
            variables: {
                input: {
                    reportId: props.report.id,
                    comment: comment.value,
                    userId: currentUser.value.id,
                    firstname: currentUser.value.firstname,
                    lastname: currentUser.value.lastname,
                    email: currentUser.value.email,
                    phone: currentUser.value.phone,
                    phoneCountry: currentUser.value.phoneCountry
                }
            }
        }
    );

    if (response.data && response.data.createComment) {
        comments.value.push(new Comment(response.data.createComment));
        comment.value = '';
    }
};

onMounted((): void => {
    getManyComments();
});

watch(comments, async (): Promise<void> => {
    if (!conversationRef.value) {
        return;
    }
    await nextTick();
    conversationRef.value.scrollTo({
        top: conversationRef.value.scrollHeight,
        behavior: 'smooth'
    });
});
</script>

<style scoped lang="scss">
@use 'ReportDetails.scss';
</style>